import { Users, ShieldCheck, Building, Eye } from 'lucide-react';

// Static class lookups — Tailwind's JIT scanner can't resolve dynamic
// `bg-${c.color}-500/10` patterns, so the runtime would silently lose the
// styling if the template strings happened to be the only references in
// the bundle. Using full literal strings keeps the generated CSS reliable.
const colorClasses = {
  rose: {
    iconBg: 'bg-rose-500/10 ring-rose-500/20',
    iconColor: 'text-rose-400',
  },
  violet: {
    iconBg: 'bg-violet-500/10 ring-violet-500/20',
    iconColor: 'text-violet-400',
  },
  cyan: {
    iconBg: 'bg-cyan-500/10 ring-cyan-500/20',
    iconColor: 'text-cyan-400',
  },
} as const;

type CaseColor = keyof typeof colorClasses;

const cases: Array<{
  icon: typeof Users;
  color: CaseColor;
  title: string;
  pitch: string;
  example: string;
  cost: string;
}> = [
  {
    icon: Users,
    color: 'rose',
    title: 'Lead discovery',
    pitch:
      'Find decision-makers at small businesses in any geography, with size + industry + role filters. Optional phone-verify the most important contacts. Push directly to your CRM.',
    example:
      '"Find IT decision-makers at 5–50-employee businesses in Greenwood, IN. Tag cold-outreach-Q2. Pipeline: Cold Outbound 2026, owner: Tom."',
    cost: '~$5–15 per job (often within your starter credit)',
  },
  {
    icon: ShieldCheck,
    color: 'violet',
    title: 'Vendor due diligence',
    pitch:
      'Verify business registration, sanctions/OFAC screening, BBB rating, recent news, leadership changes, basic litigation history. One-page risk report.',
    example:
      '"Verify Acme Industries (Texas). Risk dimensions: sanctions, litigation, financial health, leadership changes."',
    cost: '~$3–7 per report',
  },
  {
    icon: Building,
    color: 'cyan',
    title: 'Telecom tax research',
    pitch:
      'State, county, and city tax rates for any US jurisdiction. Auto-applies high-confidence rates from primary sources, queues ambiguous ones for review. The proof-of-concept that built this product.',
    example: '"Refresh telecom tax rates for all 52 jurisdictions, federal + state + local."',
    cost: 'Under $2 for a full 52-jurisdiction sweep',
  },
  {
    icon: Eye,
    color: 'rose',
    title: 'Competitor monitoring',
    pitch:
      'Track competitor websites for changes — pricing, products, leadership, hiring, reviews. Weekly digest with alerts on material changes. Sly mode (rotating UA, public pages only).',
    example: '"Watch Acme.com weekly: pricing, careers, blog, social. Alert me on any pricing change."',
    cost: '~$1–3 per competitor per week',
  },
];

export default function UseCasesSection() {
  return (
    <section id="use-cases" className="relative bg-ink-900 py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-rose-400">
            Use cases
          </p>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Built for the back-office work everyone hates doing manually.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Templates ship with the product — and you can build your own.
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {cases.map((c) => {
            const Icon = c.icon;
            const colors = colorClasses[c.color];
            return (
              <div
                key={c.title}
                className="card-hover rounded-2xl border border-white/10 bg-ink-950/60 p-8"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className={`grid h-10 w-10 place-items-center rounded-lg ring-1 ${colors.iconBg}`}>
                    <Icon className={`h-5 w-5 ${colors.iconColor}`} strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{c.title}</h3>
                </div>
                <p className="mb-4 text-sm leading-relaxed text-slate-400">{c.pitch}</p>
                <div className="mb-4 rounded-lg border border-white/5 bg-white/5 p-4">
                  <p className="font-mono text-xs leading-relaxed text-slate-300">
                    {c.example}
                  </p>
                </div>
                <p className="text-xs font-medium text-rose-300">{c.cost}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
